// Opener-liveness guard for WindowPortal popups (VNC / Moonlight / SSH).
//
// The React tree driving a popup runs in the DASHBOARD tab. If that tab
// reloads, navigates away or is closed without `pagehide` firing, the popup
// survives as a frozen ghost (spinner forever, no retry). The popup therefore
// polls "is the document that opened me still alive?" and closes itself
// when it isn't.
//
// Why a token on the opener window instead of a heartbeat: the previous
// guard had the opener stamp the popup every second and the popup close when
// the stamp was >15s old. Chrome (151, Aug 2026) intensively throttles a
// hidden tab's timers to ONE wake-up per minute after ~60s in the background
// — so the first time a user left the dashboard tab for a minute, the beats
// paused for ~60s and the popup killed a perfectly healthy session (prod
// backend logs: healthy tunnels closed browser-side after exactly 75.9s).
// Reading a property off `window.opener` needs no timers on the opener side:
//   - opener reloaded/navigated → fresh Window object, token gone → close
//   - opener tab closed          → `opener.closed` (or null opener) → close
//   - opener moved cross-origin  → property access throws → close
//   - opener merely hidden/throttled/frozen → token still there → stay open
export const OPENER_TOKENS_KEY = '__portalTokens';

export function mintPortalToken() {
  const rand =
    (typeof crypto !== 'undefined' && crypto.randomUUID && crypto.randomUUID()) ||
    `${Math.random()}-${Date.now()}`;
  return `portal-${rand}`;
}

// Register `token` on the opener window; returns an unregister function.
export function registerPortalToken(openerWindow, token) {
  const bag = (openerWindow[OPENER_TOKENS_KEY] = openerWindow[OPENER_TOKENS_KEY] || {});
  bag[token] = true;
  return () => {
    try { delete bag[token]; } catch (_) { /* opener already gone */ }
  };
}

// Script text injected into the popup document. Self-contained: it must not
// reference anything from the opener's bundle, only `window.opener`.
export function popupGuardScript(token, { intervalMs = 2000 } = {}) {
  return (
    '(function () {' +
    `  var TOKEN = ${JSON.stringify(token)};` +
    `  var KEY = ${JSON.stringify(OPENER_TOKENS_KEY)};` +
    '  function openerAlive() {' +
    '    try {' +
    '      var o = window.opener;' +
    '      return !!(o && !o.closed && o[KEY] && o[KEY][TOKEN]);' +
    '    } catch (_) { return false; }' +
    '  }' +
    '  setInterval(function () { if (!openerAlive()) window.close(); }, ' + intervalMs + ');' +
    '})();'
  );
}
